export const AUTH0_SIGNUP_PARAM = 'auth0Signup';

/**
 * Global set on the server-rendered admin page by sonder-rails when the admin has an
 * Auth0 session (see app/views/shared/_admin_auth0_boot.html.slim). Holds the Auth0
 * access token the Rails session obtained, so the embedded admin Angular widgets can
 * authenticate against the monolith API without running an in-browser Auth0 login.
 * This is the contract between the Rails admin shell and the admin portal SPA.
 */
export const ADMIN_AUTH0_TOKEN_GLOBAL = 'SONDERMIND_ADMIN_AUTH0_TOKEN';

/**
 * Query param marker indicating a returning user signed in from the public quick-checkout
 * "Sign in" link and should be deep-linked into the client-portal direct-booking flow.
 * Set on the Auth0 redirect_uri by the provider-landing app and read by client-portal post-login.
 */
export const BOOK_INTENT_PARAM = 'bookIntent';

/**
 * Query param keys carrying the booking-intent payload across the cross-domain Auth0 login.
 * Written on the redirect_uri by the public quick-checkout "Sign in" link and read by
 * client-portal post-login to open the direct-booking modal for the same provider/slot.
 */
export enum BookIntentParam {
  PROVIDER_ID = 'bookProviderId',
  START = 'bookStart',
  END = 'bookEnd',
  SESSION_TYPE = 'bookSessionType'
}

/**
 * Query param marker indicating a client is arriving from the "Matches on Matchflow" flow: they
 * picked a provider (and optionally a timeslot) in the pre-signup matchflow, then completed Auth0
 * signup. Set on the Auth0 redirect_uri by IntakeSignUpComponent and read by client-portal
 * post-login to open the direct-booking modal, pre-filled, for the same provider/slot. Distinct
 * from BOOK_INTENT_PARAM because that intent always opens the NEW_CLIENT flow, while matchflow
 * needs EXISTING_CLIENT with a resolved ClientReferralMember.
 */
export const MATCHFLOW_BOOKING_PARAM = 'matchflowBooking';

/**
 * Query param keys carrying the matchflow booking payload across the cross-domain Auth0 signup.
 * Written on the redirect_uri by IntakeSignUpComponent and read by client-portal post-login.
 * PROVIDER_SHARE_CODE lets client-portal deep-link straight to the provider's profile page
 * (MatchflowBookingGuard) to finish booking.
 */
export enum MatchflowBookingParam {
  PROVIDER_SHARE_CODE = 'matchflowProviderShareCode',
  SLOT_START = 'matchflowSlotStart',
  SLOT_END = 'matchflowSlotEnd',
  SLOT_SESSION_TYPE = 'matchflowSlotSessionType'
}

/**
 * Query param marker telling the client-portal provider-profile page to auto-open the
 * direct-booking modal on load (rather than waiting for a manual "Schedule" click). Set by
 * BookingIntentService when it deep-links a matchflow client to /providers/:shareCode so the whole
 * booking/account-completion flow happens from the provider's profile. Distinct from the profile
 * page's existing `requestSession` param, which triggers the propose-a-time path instead.
 */
export const PROFILE_DIRECT_BOOKING_PARAM = 'directBooking';

/**
 * Slot query-param keys read by the provider-profile page alongside PROFILE_DIRECT_BOOKING_PARAM to
 * pre-fill the auto-opened direct-booking modal. Optional: absent slot opens the time-picker.
 */
export enum ProfileDirectBookingParam {
  SLOT_START = 'directBookingSlotStart',
  SLOT_END = 'directBookingSlotEnd',
  SLOT_SESSION_TYPE = 'directBookingSlotSessionType'
}
